import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, List, ListItemButton, ListItemText,
  MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Plus, Trash2, Pencil, ArrowRight, GripVertical, RotateCcw } from "lucide-react";
import { GlassCard, PersonAvatar, T, TYPE, formatRelative, labelSx, primaryButtonSx, ghostButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { TypeIcon, has } from "../ui";
import { MultiSelect, PersonPicker, Select, dateInput, fieldSx } from "../inputs";
import { ActionRows, ConditionRows } from "../RuleEditors";

const Section = ({ title, subtitle, children, action }) => (
  <GlassCard sx={{ p: 2.5, mb: 2.5 }}>
    <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
    {children}
  </GlassCard>
);

// ---------------------------------------------------------------------------
const ADMIN_FIELDS = ["status", "visibility", "teamId", "typeId", "startDate", "endDate", "color"];
const Details = ({ project, reloadProject, canEdit, canSettings }) => {
  const ctx = usePm();
  // Lead and manager: everyone sees them, only admins / account managers assign them.
  const canAssignLead = canEdit && Boolean(ctx.me.isManager);
  // Status, visibility, team, type, dates and colour: admins only.
  const canAdminFields = canEdit && Boolean(ctx.me.isAdminRole);
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      name: project.name, description: String(project.description || "").replace(/<[^>]+>/g, " ").trim(), leadId: String(project.lead?.id || ""), managerId: project.manager?.id ? String(project.manager.id) : null,
      teamId: project.teamId || null, visibility: project.visibility, status: project.status, startDate: dateInput(project.startDate), endDate: dateInput(project.endDate), color: project.color,
      typeId: project.typeId || null, estimation: project.estimation, sprintsEnabled: project.sprintsEnabled, allowParallelSprints: project.allowParallelSprints, defaultSprintDays: project.defaultSprintDays,
      issueTypeIds: (project.issueTypeIds || []).map(String),
    });
  }, [project]);
  const save = async () => {
    try {
      const body = { ...f, startDate: f.startDate || null, endDate: f.endDate || null, description: f.description ? `<p>${f.description.replace(/[<>&]/g, "")}</p>` : "" };
      if (!canSettings) ["estimation", "sprintsEnabled", "allowParallelSprints", "issueTypeIds"].forEach((k) => delete body[k]);
      if (!canAssignLead) ["leadId", "managerId"].forEach((k) => delete body[k]);
      if (!canAdminFields) ADMIN_FIELDS.forEach((k) => delete body[k]);
      await pm.updateProject(project._id, body);
      ctx.notify("Project saved");
      reloadProject();
    } catch (err) {
      ctx.fail(err);
    }
  };
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Section title="Details" action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button>}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Name" value={f.name || ""} onChange={(e) => set("name")(e.target.value)} disabled={!canEdit} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Key" value={project.key} disabled helperText="Keys cannot change (issue keys depend on them)" sx={fieldSx} /></Grid>
        <Grid item xs={12}><TextField fullWidth multiline minRows={2} size="small" label="Description" value={f.description || ""} onChange={(e) => set("description")(e.target.value)} disabled={!canEdit} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={6}><PersonPicker label="Lead" value={f.leadId} onChange={set("leadId")} allowEmpty={false} disabled={!canAssignLead} helperText={canAssignLead ? undefined : "Assigned by an admin or account manager"} /></Grid>
        <Grid item xs={12} sm={6}><PersonPicker label="Manager" value={f.managerId} onChange={set("managerId")} disabled={!canAssignLead} helperText={canAssignLead ? undefined : "Assigned by an admin or account manager"} /></Grid>
        <Grid item xs={12} sm={4}><Select label="Status" value={f.status} onChange={set("status")} disabled={!canAdminFields} options={ctx.settings.projectStatuses.map((s) => ({ value: s, label: s }))} /></Grid>
        <Grid item xs={12} sm={4}><Select label="Visibility" value={f.visibility} onChange={set("visibility")} disabled={!canAdminFields} options={["private", "team", "shared", "internal"].map((v) => ({ value: v, label: v }))} /></Grid>
        <Grid item xs={12} sm={4}><Select label="Team" value={f.teamId} onChange={set("teamId")} disabled={!canAdminFields} allowEmpty options={ctx.teams.map((t) => ({ value: t._id, label: t.name }))} /></Grid>
        <Grid item xs={12} sm={4}><Select label="Project type" value={f.typeId} onChange={set("typeId")} disabled={!canAdminFields} allowEmpty options={ctx.projectTypes.map((t) => ({ value: t._id, label: t.name }))} /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={f.startDate || ""} onChange={(e) => set("startDate")(e.target.value)} disabled={!canAdminFields} sx={fieldSx} /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={f.endDate || ""} onChange={(e) => set("endDate")(e.target.value)} disabled={!canAdminFields} sx={fieldSx} /></Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="color" label="Colour" value={f.color || "#6366F1"} onChange={(e) => set("color")(e.target.value)} disabled={!canAdminFields} sx={fieldSx} /></Grid>
      </Grid>
      {canSettings && (
        <>
          <Typography sx={{ ...labelSx, mt: 3, mb: 1 }}>Work configuration</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}><Select label="Estimation" value={f.estimation} onChange={set("estimation")} options={[{ value: "points", label: "Story points" }, { value: "hours", label: "Hours" }, { value: "count", label: "Issue count" }]} /></Grid>
            <Grid item xs={12} sm={3}><FormControlLabel control={<Switch checked={Boolean(f.sprintsEnabled)} onChange={(e) => set("sprintsEnabled")(e.target.checked)} />} label="Sprints" /></Grid>
            <Grid item xs={12} sm={3}><FormControlLabel control={<Switch checked={Boolean(f.allowParallelSprints)} onChange={(e) => set("allowParallelSprints")(e.target.checked)} />} label="Parallel sprints" /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth size="small" type="number" label="Default sprint days" value={f.defaultSprintDays || 14} onChange={(e) => set("defaultSprintDays")(Number(e.target.value))} sx={fieldSx} /></Grid>
            <Grid item xs={12}><MultiSelect label="Issue types in this project" value={f.issueTypeIds || []} onChange={set("issueTypeIds")} options={ctx.issueTypes.filter((t) => t.isActive).map((t) => ({ value: String(t._id), label: t.name }))} /></Grid>
          </Grid>
        </>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const Members = ({ project, reloadProject }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState({ memberId: null, roleId: ctx.projectRoles.find((r) => r.key === "developer")?._id });
  const canManage = has(project, "project.manage_members") && !project.archivedAt;
  const load = () => pm.members(project._id).then(setRows).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id]);
  const memberIds = new Set(rows.map((r) => String(r.member.id)));
  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) ctx.notify(msg);
      load();
      reloadProject();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Section title="Members & roles" subtitle="What each person can do here comes from their project role. Non-members see the project only through its visibility.">
      {canManage && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}><PersonPicker label="Add person" value={add.memberId} onChange={(id) => setAdd({ ...add, memberId: id })} people={ctx.people.filter((p) => !memberIds.has(String(p.id)))} /></Box>
          <Box sx={{ minWidth: 180 }}><Select label="Role" value={add.roleId} onChange={(id) => setAdd({ ...add, roleId: id })} options={ctx.projectRoles.map((r) => ({ value: r._id, label: r.name }))} /></Box>
          <Button variant="contained" sx={primaryButtonSx} disabled={!add.memberId} onClick={() => act(() => pm.addMember(project._id, add), "Member added").then(() => setAdd({ ...add, memberId: null }))}>Add</Button>
        </Stack>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{["Person", "Role", "Notifications", "Capacity (h/day)", "Added", ""].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((m) => {
              const self = String(m.member.id) === String(ctx.me.id);
              return (
                <TableRow key={m._id}>
                  <TableCell><Stack direction="row" spacing={1} alignItems="center"><PersonAvatar name={m.member.name} size={26} /><Typography sx={{ fontSize: TYPE.small }}>{m.member.name}{self ? " (you)" : ""}</Typography></Stack></TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    {canManage ? <Select value={String(m.roleId)} onChange={(roleId) => act(() => pm.updateMember(project._id, m._id, { roleId }), "Role changed")} options={ctx.projectRoles.map((r) => ({ value: r._id, label: r.name }))} /> : m.role?.name}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {canManage || self ? <Select value={m.notify} onChange={(notify) => act(() => pm.updateMember(project._id, m._id, { notify }))} options={[["all", "All activity"], ["participating", "Participating"], ["mentions", "Mentions only"], ["none", "None"]].map(([v, l]) => ({ value: v, label: l }))} /> : m.notify}
                  </TableCell>
                  <TableCell>
                    {canManage ? <TextField size="small" type="number" defaultValue={m.capacity?.hoursPerDay ?? ""} onBlur={(e) => act(() => pm.updateMember(project._id, m._id, { capacity: { ...(m.capacity || {}), hoursPerDay: Number(e.target.value) || 0 } }))} sx={{ ...fieldSx, width: 90 }} /> : m.capacity?.hoursPerDay ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(m.createdAt)}</TableCell>
                  <TableCell>{(canManage || self) && <IconButton size="small" onClick={() => window.confirm(self ? "Leave this project?" : `Remove ${m.member.name}?`) && act(() => pm.removeMember(project._id, m._id), "Removed")}><Trash2 size={14} /></IconButton>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

// ---------------------------------------------------------------------------
const VALIDATORS = [["required_resolution", "Resolution required"], ["required_comment", "Comment required"], ["required_field", "Field required"], ["assignee_required", "Assignee required"], ["required_attachment", "Attachment required"]];

const TransitionDialog = ({ t, statusIds, onClose, onSave }) => {
  const ctx = usePm();
  const [f, setF] = useState(null);
  useEffect(() => {
    if (t) setF(JSON.parse(JSON.stringify({ name: "", from: [], to: statusIds[0], permission: "issue.transition", conditions: { assigneeOnly: false, memberOnly: true, rules: { all: [], any: [] } }, validators: [], actions: [], ...t })));
  }, [t, statusIds]);
  if (!t || !f) return null;
  const statusOpts = statusIds.map((id) => ({ value: String(id), label: ctx.statusById.get(String(id))?.name || "?" }));
  const perms = ctx.projectRoles.flatMap((r) => r.permissions).filter((p, i, a) => a.indexOf(p) === i).sort();
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t._id ? `Edit “${t.name}”` : "New transition"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Button name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><Select label="Required permission" value={f.permission} onChange={(permission) => setF({ ...f, permission })} options={perms.map((p) => ({ value: p, label: p }))} /></Grid>
          <Grid item xs={12} sm={7}><MultiSelect label="From (empty = any status)" value={(f.from || []).map(String)} onChange={(from) => setF({ ...f, from })} options={statusOpts} /></Grid>
          <Grid item xs={12} sm={5}><Select label="To" value={String(f.to)} onChange={(to) => setF({ ...f, to })} options={statusOpts} /></Grid>
          <Grid item xs={12}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Conditions (who can use it)</Typography>
            <FormControlLabel control={<Checkbox size="small" checked={f.conditions.assigneeOnly} onChange={(e) => setF({ ...f, conditions: { ...f.conditions, assigneeOnly: e.target.checked } })} />} label={<Typography sx={{ fontSize: TYPE.small }}>Only the assignee (and project admins)</Typography>} />
            <FormControlLabel control={<Checkbox size="small" checked={f.conditions.memberOnly} onChange={(e) => setF({ ...f, conditions: { ...f.conditions, memberOnly: e.target.checked } })} />} label={<Typography sx={{ fontSize: TYPE.small }}>Only project members</Typography>} />
            <ConditionRows value={f.conditions.rules} onChange={(rules) => setF({ ...f, conditions: { ...f.conditions, rules } })} />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Validators (what must be provided)</Typography>
            {f.validators.map((v, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ mb: 0.75 }} alignItems="center">
                <TextField select size="small" value={v.type} onChange={(e) => setF({ ...f, validators: f.validators.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)) })} sx={{ ...fieldSx, minWidth: 200 }}>
                  {VALIDATORS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                </TextField>
                {v.type === "required_field" && <TextField size="small" label="Field" placeholder="dueDate / storyPoints / cf.key" value={v.field || ""} onChange={(e) => setF({ ...f, validators: f.validators.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })} sx={fieldSx} />}
                <TextField size="small" label="Message" value={v.message || ""} onChange={(e) => setF({ ...f, validators: f.validators.map((x, j) => (j === i ? { ...x, message: e.target.value } : x)) })} sx={{ ...fieldSx, flexGrow: 1 }} />
                <IconButton size="small" onClick={() => setF({ ...f, validators: f.validators.filter((_, j) => j !== i) })}><Trash2 size={14} /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<Plus size={13} />} onClick={() => setF({ ...f, validators: [...f.validators, { type: "required_comment", field: "", message: "" }] })}>Add validator</Button>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Post actions (run after the transition)</Typography>
            <ActionRows value={f.actions} onChange={(actions) => setF({ ...f, actions })} exclude={["transition", "project_summary"]} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!f.name || !f.to} onClick={() => onSave(f)}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

const WorkflowEditor = ({ project, reloadProject }) => {
  const ctx = usePm();
  const [wf, setWf] = useState(null);
  const [edit, setEdit] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const canEdit = has(project, "project.manage_settings") && !project.archivedAt;
  useEffect(() => {
    pm.workflow(project.workflowId).then(setWf).catch((err) => setError(errorMessage(err)));
  }, [project.workflowId]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!wf) return null;
  const change = (patch) => {
    setWf((w) => ({ ...w, ...patch }));
    setDirty(true);
  };
  const save = async () => {
    try {
      const saved = await pm.saveWorkflow(wf._id, { statusIds: wf.statusIds, initialStatusId: wf.initialStatusId, transitions: wf.transitions });
      setWf(saved);
      setDirty(false);
      ctx.notify("Workflow saved");
      reloadProject();
    } catch (err) {
      ctx.fail(err, "Workflow not saved");
    }
  };
  const name = (id) => ctx.statusById.get(String(id))?.name || "?";
  return (
    <Section title="Workflow" subtitle="Statuses issues move through, and the transitions (buttons) between them with their conditions, validators and post actions."
      action={canEdit && <Button variant="contained" sx={primaryButtonSx} disabled={!dirty} onClick={save}>Save workflow</Button>}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}><MultiSelect label="Statuses in this workflow" value={wf.statusIds.map(String)} onChange={(statusIds) => change({ statusIds })} options={ctx.statuses.filter((s) => s.isActive).map((s) => ({ value: String(s._id), label: `${s.name} (${s.category})` }))} /></Grid>
        <Grid item xs={12} md={4}><Select label="Initial status" value={String(wf.initialStatusId)} onChange={(initialStatusId) => change({ initialStatusId })} options={wf.statusIds.map((id) => ({ value: String(id), label: name(id) }))} /></Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {wf.statusIds.map((id) => <Chip key={id} label={name(id)} size="small" sx={{ bgcolor: `${ctx.statusById.get(String(id))?.color}22`, border: `1px solid ${ctx.statusById.get(String(id))?.color}` }} />)}
      </Stack>
      <Table size="small">
        <TableHead><TableRow>{["Transition", "From → To", "Rules", ""].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {wf.transitions.map((t, i) => (
            <TableRow key={t._id || i}>
              <TableCell sx={{ fontWeight: 600, fontSize: TYPE.small }}><GripVertical size={12} color={T.inkMuted} /> {t.name}</TableCell>
              <TableCell sx={{ fontSize: TYPE.small }}>{t.from?.length ? t.from.map(name).join(", ") : "Any"} <ArrowRight size={12} /> <b>{name(t.to)}</b></TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {t.conditions?.assigneeOnly && <Chip size="small" label="assignee only" sx={{ height: 18, fontSize: "0.62rem" }} />}
                  {(t.conditions?.rules?.all?.length || t.conditions?.rules?.any?.length) > 0 && <Chip size="small" label="field rules" sx={{ height: 18, fontSize: "0.62rem" }} />}
                  {(t.validators || []).map((v, j) => <Chip key={j} size="small" label={v.type.replace("required_", "needs ")} sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#FEF0C7" }} />)}
                  {(t.actions || []).map((a, j) => <Chip key={`a${j}`} size="small" label={a.type} sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#E0F2FE" }} />)}
                </Stack>
              </TableCell>
              <TableCell align="right">
                {canEdit && <IconButton size="small" onClick={() => setEdit({ ...t, _index: i })}><Pencil size={14} /></IconButton>}
                {canEdit && <IconButton size="small" onClick={() => change({ transitions: wf.transitions.filter((_, j) => j !== i) })}><Trash2 size={14} /></IconButton>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canEdit && <Button size="small" startIcon={<Plus size={13} />} sx={{ mt: 1 }} onClick={() => setEdit({ _index: -1 })}>Add transition</Button>}
      <TransitionDialog t={edit} statusIds={wf.statusIds} onClose={() => setEdit(null)}
        onSave={(t) => {
          const { _index, ...clean } = t;
          change({ transitions: _index >= 0 ? wf.transitions.map((x, j) => (j === _index ? clean : x)) : [...wf.transitions, { ...clean, order: wf.transitions.length }] });
          setEdit(null);
        }} />
    </Section>
  );
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
const Components = ({ project }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => pm.components(project._id).then(setRows).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id]);
  const save = async () => {
    try {
      const body = { name: edit.name, description: edit.description, leadId: edit.leadId || null, defaultAssignee: edit.defaultAssignee };
      if (edit._id) await pm.updateComponent(edit._id, body);
      else await pm.createComponent(project._id, body);
      setEdit(null);
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Section title="Components" subtitle="Parts of the product. A component lead can become the default assignee."
      action={<Button size="small" startIcon={<Plus size={13} />} onClick={() => setEdit({ name: "", description: "", leadId: null, defaultAssignee: "none" })}>Add</Button>}>
      {!rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No components.</Typography>}
      <List dense>
        {rows.map((c) => (
          <ListItemButton key={c._id} onClick={() => setEdit({ ...c, leadId: c.lead?.id ? String(c.lead.id) : null })}>
            <ListItemText primary={c.name} secondary={`${c.lead?.name ? `Lead ${c.lead.name} · ` : ""}default assignee: ${c.defaultAssignee}`} />
            <IconButton size="small" onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Delete ${c.name}?`)) { await pm.deleteComponent(c._id); load(); } }}><Trash2 size={14} /></IconButton>
          </ListItemButton>
        ))}
      </List>
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{edit?._id ? "Edit component" : "New component"}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Stack spacing={2}>
              <TextField size="small" label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} sx={fieldSx} />
              <TextField size="small" label="Description" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} sx={fieldSx} />
              <PersonPicker label="Lead" value={edit.leadId} onChange={(leadId) => setEdit({ ...edit, leadId })} />
              <Select label="Default assignee" value={edit.defaultAssignee} onChange={(defaultAssignee) => setEdit({ ...edit, defaultAssignee })} options={[["none", "None"], ["lead", "Component lead"], ["project_lead", "Project lead"]].map(([v, l]) => ({ value: v, label: l }))} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} disabled={!edit?.name} onClick={save}>Save</Button></DialogActions>
      </Dialog>
    </Section>
  );
};

// ---------------------------------------------------------------------------
export const AutomationRules = ({ project, boardId = null }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [runs, setRuns] = useState(null);
  const load = () => pm.automation(project?._id, boardId || undefined).then((r) => setRows(project ? r.filter((x) => String(x.projectId) === String(project._id) && (!boardId || String(x.boardId) === String(boardId))) : r.filter((x) => !x.projectId))).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?._id, boardId]);
  const save = async () => {
    try {
      const { _id, runCount, lastRunAt, createdAt, updatedAt, createdBy, clientId, __v, ...body } = edit;
      await pm.saveAutomation(_id, { ...body, projectId: project?._id || null, ...(boardId ? { boardId } : {}) });
      setEdit(null);
      ctx.notify("Rule saved");
      load();
    } catch (err) {
      ctx.fail(err, "Rule not saved");
    }
  };
  const TRIGGERS = [["issue.created", "Issue created"], ["issue.updated", "Issue updated"], ["issue.transitioned", "Status changed"], ["issue.assigned", "Issue assigned"], ["comment.added", "Comment added"], ["sprint.started", "Sprint started"], ["sprint.completed", "Sprint completed"], ["version.released", "Version released"], ["scheduled", "Scheduled"]];
  return (
    <Section title={boardId ? "Board automation" : project ? "Automation" : "Global automation"} subtitle={boardId ? "Rules here only act on issues on this board. Tip: WHEN status changed IF changes.statusCategory.to = done THEN …; scheduled rules can use daysInStatus ≥ 3." : "WHEN something happens IF conditions match THEN run actions. Rules cannot trigger each other more than 3 levels deep."}
      action={<Button size="small" startIcon={<Plus size={13} />} onClick={() => setEdit({ name: "", trigger: "issue.created", enabled: true, conditions: { all: [], any: [] }, actions: [], schedule: { frequency: "daily", hour: 9, weekday: 1, timezone: "Asia/Kolkata" } })}>New rule</Button>}>
      {!rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No rules yet.</Typography>}
      {rows.map((r) => (
        <Stack key={r._id} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, borderBottom: `1px solid ${T.border}` }}>
          <Switch size="small" checked={r.enabled} onChange={async (e) => { await pm.saveAutomation(r._id, { enabled: e.target.checked }); load(); }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{r.name}</Typography>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>When {TRIGGERS.find((t) => t[0] === r.trigger)?.[1]} · {r.actions.map((a) => a.type).join(", ")} · ran {r.runCount} times{r.lastRunAt ? ` · last ${formatRelative(r.lastRunAt)}` : ""}</Typography>
          </Box>
          <Button size="small" onClick={() => pm.automationRuns(r._id).then((x) => setRuns({ rule: r, rows: x }))}>Log</Button>
          <IconButton size="small" onClick={() => setEdit(JSON.parse(JSON.stringify(r)))}><Pencil size={14} /></IconButton>
          <IconButton size="small" onClick={async () => { if (window.confirm(`Delete rule "${r.name}"?`)) { await pm.deleteAutomation(r._id); load(); } }}><Trash2 size={14} /></IconButton>
        </Stack>
      ))}
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{edit?._id ? "Edit rule" : "New rule"}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Stack spacing={2}>
              <TextField size="small" label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} sx={fieldSx} />
              <Select label="When" value={edit.trigger} onChange={(trigger) => setEdit({ ...edit, trigger })} options={TRIGGERS.map(([v, l]) => ({ value: v, label: l }))} />
              {edit.trigger === "scheduled" && (
                <Stack direction="row" spacing={1.5}>
                  <Select label="Frequency" value={edit.schedule?.frequency} onChange={(frequency) => setEdit({ ...edit, schedule: { ...edit.schedule, frequency } })} options={["hourly", "daily", "weekly"].map((v) => ({ value: v, label: v }))} />
                  {edit.schedule?.frequency !== "hourly" && <TextField size="small" type="number" label="Hour (0–23)" value={edit.schedule?.hour ?? 9} onChange={(e) => setEdit({ ...edit, schedule: { ...edit.schedule, hour: Number(e.target.value) } })} sx={fieldSx} />}
                  {edit.schedule?.frequency === "weekly" && <Select label="Weekday" value={edit.schedule?.weekday ?? 1} onChange={(weekday) => setEdit({ ...edit, schedule: { ...edit.schedule, weekday: Number(weekday) } })} options={["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => ({ value: i, label: d }))} />}
                </Stack>
              )}
              <Box><Typography sx={{ ...labelSx, mb: 0.5 }}>If</Typography><ConditionRows value={edit.conditions} onChange={(conditions) => setEdit({ ...edit, conditions })} /></Box>
              <Box><Typography sx={{ ...labelSx, mb: 0.5 }}>Then</Typography><ActionRows value={edit.actions} onChange={(actions) => setEdit({ ...edit, actions })} /></Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} disabled={!edit?.name || !edit?.actions?.length} onClick={save}>Save rule</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(runs)} onClose={() => setRuns(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Runs · {runs?.rule.name}</DialogTitle>
        <DialogContent dividers>
          {!runs?.rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No runs yet.</Typography>}
          {runs?.rows.map((x) => (
            <Box key={x._id} sx={{ py: 0.75, borderBottom: `1px solid ${T.border}` }}>
              <Typography sx={{ fontSize: TYPE.small }}>{x.matched ? "Matched" : "Skipped (conditions not met)"} · {formatRelative(x.createdAt)} · {x.durationMs}ms</Typography>
              {x.actions.map((a, i) => <Typography key={i} sx={{ fontSize: TYPE.micro, color: a.ok ? "#067647" : "#B42318" }}>{a.type}: {a.ok ? "ok" : a.error}</Typography>)}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Section>
  );
};

// ---------------------------------------------------------------------------
const Danger = ({ project, reloadProject }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  return (
    <Section title="Archive or delete">
      {has(project, "project.archive") && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}><Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{project.archivedAt ? "Restore project" : "Archive project"}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>Archived projects are read-only and hidden from lists by default.</Typography></Box>
          <Button variant="outlined" sx={ghostButtonSx} onClick={async () => { try { await pm.archiveProject(project._id, !project.archivedAt); ctx.notify(project.archivedAt ? "Project restored" : "Project archived"); reloadProject(); } catch (err) { ctx.fail(err); } }}>{project.archivedAt ? "Restore" : "Archive"}</Button>
        </Stack>
      )}
      {has(project, "project.delete") && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Box sx={{ flexGrow: 1 }}><Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: "#B42318" }}>Delete project</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>Moves the project to the trash (an admin can restore it). Type the key <b>{project.key}</b> to confirm.</Typography></Box>
          <TextField size="small" placeholder={project.key} value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} sx={{ ...fieldSx, width: 140 }} />
          <Button variant="contained" color="error" disabled={confirm !== project.key} onClick={async () => { try { await pm.deleteProject(project._id, { confirmKey: confirm }); ctx.notify(`${project.key} moved to trash`); navigate("/projects"); } catch (err) { ctx.fail(err); } }}>Delete</Button>
        </Stack>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
/** Deleted issues of this project; sub-tasks deleted with their parent come back with it. */
const Trash = ({ project }) => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => pm.trashedIssues(project._id).then(setRows).catch((err) => { setRows([]); ctx.fail(err); });
  useEffect(() => {
    load();
  }, [project._id]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (i) => {
    setBusy(i._id);
    try {
      await pm.restoreIssue(i._id);
      ctx.notify(`${i.key} restored${i.subtasks?.length ? ` with ${i.subtasks.length} sub-task${i.subtasks.length > 1 ? "s" : ""}` : ""}`);
      await load();
    } catch (err) {
      ctx.fail(err, "Could not restore");
    } finally {
      setBusy(null);
    }
  };
  const canRestore = has(project, "issue.delete");
  return (
    <Section title="Trash" subtitle="Deleted issues stay here until restored. Restoring an issue also brings back the sub-tasks that were deleted with it.">
      {rows === null && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Loading…</Typography>}
      {rows && !rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>The trash is empty.</Typography>}
      {rows && rows.length > 0 && (
        <Table size="small">
          <TableHead><TableRow>{["Issue", "Deleted", "By", ""].map((h, i) => <TableCell key={i} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((i) => (
              <TableRow key={i._id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <TypeIcon typeId={i.typeId} />
                    <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.inkSecondary, whiteSpace: "nowrap" }}>{i.key}</Typography>
                    <Typography noWrap sx={{ fontSize: TYPE.small }}>{i.summary}</Typography>
                  </Stack>
                  {i.subtasks?.length > 0 && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, ml: 3.5 }}>+ {i.subtasks.map((s) => s.key).join(", ")}</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize: TYPE.small, whiteSpace: "nowrap" }}>{formatRelative(i.deletedAt)}</TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{i.deletedBy?.name || "—"}</TableCell>
                <TableCell align="right">
                  {canRestore && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<RotateCcw size={14} />} disabled={busy === i._id} onClick={() => restore(i)}>Restore</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
};

const SECTIONS = [["details", "Details"], ["members", "Members"], ["workflow", "Workflow"], ["components", "Components"], ["automation", "Automation"], ["trash", "Trash"], ["danger", "Archive / delete"]];

const ProjectSettings = (props) => {
  const { project } = props;
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "details";
  const canEdit = has(project, "project.update") && !project.archivedAt;
  const canSettings = has(project, "project.manage_settings") && !project.archivedAt;
  const visible = SECTIONS.filter(([k]) => ({
    details: true, members: true, workflow: has(project, "project.manage_settings"), components: has(project, "project.manage_settings"),
    automation: has(project, "automation.manage"), trash: has(project, "issue.delete"), danger: has(project, "project.archive") || has(project, "project.delete"),
  }[k]));
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "200px minmax(0,1fr)" }, gap: 2.5 }}>
      <GlassCard sx={{ p: 1, alignSelf: "start" }}>
        <List dense>
          {visible.map(([k, l]) => <ListItemButton key={k} selected={section === k} onClick={() => setParams({ section: k }, { replace: true })}><ListItemText primary={l} primaryTypographyProps={{ fontSize: TYPE.small, fontWeight: section === k ? 700 : 500 }} /></ListItemButton>)}
        </List>
      </GlassCard>
      <Box sx={{ minWidth: 0 }}>
        {section === "details" && <Details {...props} canEdit={canEdit} canSettings={canSettings} />}
        {section === "members" && <Members {...props} />}
        {section === "workflow" && <WorkflowEditor {...props} />}
        {section === "components" && <Components {...props} />}
        {section === "automation" && <AutomationRules {...props} />}
        {section === "trash" && <Trash {...props} />}
        {section === "danger" && <Danger {...props} />}
      </Box>
    </Box>
  );
};

export default ProjectSettings;
